import os from 'node:os';
import path from 'node:path';

import { log } from '../logger.ts';

export type ClaudeHistoryEntry = {
  display?: string;
  pastedContents?: Record<string, unknown>;
  project: string;
  sessionId?: string;
  timestamp: number;
  /**
   * Absolute path to the transcript JSONL discovered via on-disk scan
   * (the `resolveExportEntry` fallback). When present, downstream
   * resolution short-circuits to this path instead of re-deriving the
   * encoded directory name from `project`. Threading the discovered
   * path through keeps disk-scan fallbacks robust if Claude Code's
   * on-disk project-dir encoding changes again.
   */
  resolvedTranscriptPath?: string;
};

export function claudeHistoryFilePath(): string {
  return path.join(os.homedir(), '.claude', 'history.jsonl');
}

export function parseAllHistoryEntries(
  historyFileContents: string,
  historyPath = claudeHistoryFilePath(),
): ClaudeHistoryEntry[] {
  const entries: ClaudeHistoryEntry[] = [];
  const lines = historyFileContents.split('\n');
  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i]?.trim();
    if (!line) continue;
    let entry: unknown;
    try {
      entry = JSON.parse(line);
    } catch (error) {
      logHistoryEntryFailure({
        historyPath,
        lineNumber: i + 1,
        line,
        reason: 'unparseable_json',
        error,
      });
      continue;
    }
    if (!isClaudeHistoryEntry(entry)) {
      logHistoryEntryFailure({
        historyPath,
        lineNumber: i + 1,
        line,
        reason: 'invalid_history_entry',
      });
      continue;
    }
    entries.push(entry);
  }
  return entries;
}

function logHistoryEntryFailure({
  historyPath,
  lineNumber,
  line,
  reason,
  error,
}: {
  historyPath: string;
  lineNumber: number;
  line: string;
  reason: 'unparseable_json' | 'invalid_history_entry';
  error?: unknown;
}): void {
  log.warn('claude_history_entry_skipped', {
    history_path: historyPath,
    line_number: lineNumber,
    history_line: line,
    reason,
    ...(error ? { error: error instanceof Error ? error.message : String(error) } : {}),
  });
}

function isClaudeHistoryEntry(value: unknown): value is ClaudeHistoryEntry {
  return (
    isRecord(value) &&
    typeof value.timestamp === 'number' &&
    typeof value.project === 'string'
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object';
}
